import { useEffect, useRef, useState } from 'react'
import { useManualQuery } from 'graphql-hooks'
import difference from 'lodash/difference'

import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectAuthUserSlice } from '../../store/auth-user-slice'
import { selectWhatsNewsSlice, setWhatsNewId as setReduxWhatsNewData, setPendingHint } from '../../store/whats-news-slice'
import { getPendingWhatsNewHintId, setPendingWhatsNewHintId } from '../../util/whats-new-hint-storage'
import { getWhatsNew as getWhatsNewQuery } from '../../graphql/custom-queries'
import { setWhatsNewAsRead } from '../../graphql/custom-mutation'
import { useIsVeeEnabled } from '../../hooks/use-is-vee-enabled'
import { IWhatsNewComponent, IWhatsNewQuery } from '@vacationtracker/shared/types/whats-new'

export interface WhatsNewPopupState {
  unreadWhatsNews: number
  showPopupNewsModal: boolean
  showPopupNews: IWhatsNewComponent | null
  closePopup: () => void
  // Closing via the announcement's own link: the user already acted, so no launcher hint.
  closePopupWithoutHint: () => void
}

export function useWhatsNewPopup(): WhatsNewPopupState {
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const { whatsNewsIds } = useAppSelector(selectWhatsNewsSlice)
  const dispatch = useAppDispatch()
  const isVeeEnabled = useIsVeeEnabled()
  const isMounted = useRef(false)

  const [getWhatsNewGqlQuery] = useManualQuery<IWhatsNewQuery, { limit: number }>(getWhatsNewQuery)
  const [setWhatsNewAsReadQuery] = useManualQuery(setWhatsNewAsRead)

  const [whatsNewData, setWhatsNewData] = useState<IWhatsNewComponent[] | []>([])
  const [unreadWhatsNews, setUnreadWhatsNews] = useState(0)
  const [showPopupNewsModal, setShowPopupNewsModal] = useState(true)
  const [showPopupNews, setShowPopupNews] = useState<IWhatsNewComponent | null>(null)

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    void getWhatsNew()
  }, [authUser.id])

  useEffect(() => {
    if (
      isMounted.current &&
      whatsNewsIds.length > 0 &&
      difference(
        whatsNewsIds,
        whatsNewData.map((item) => item.messageId)
      ).length > 0
    ) {
      void getWhatsNew()
    }
  }, [whatsNewsIds])

  const getWhatsNew = async () => {
    const response = await getWhatsNewGqlQuery({ variables: { limit: 5 } })
    if (!isMounted.current) return
    // A failed fetch just leaves the popup/badge state as-is - nothing catches a throw here.
    if (!response.data || response.error) return
    const news = response.data.getWhatsNew?.news ?? []
    setWhatsNewData(news)
    const unreadNews = news.filter((item) => item.unread)
    setUnreadWhatsNews(unreadNews.length)
    const pendingHintId = getPendingWhatsNewHintId(authUser.id)
    // A closed popup stays unread until mark-as-read lands; never re-pop it next to its launcher hint.
    const popupNews = unreadNews.find((item) => item.showPopup && item.messageId !== pendingHintId)
    if (popupNews) {
      setShowPopupNewsModal(true)
      setShowPopupNews(popupNews)
    }
    dispatch(setReduxWhatsNewData(news.map((item) => item.messageId)))
    const pendingHint = news.find((item) => item.messageId === pendingHintId)
    if (pendingHint) {
      dispatch(setPendingHint(pendingHint))
    }
  }

  const handleUnreadNews = async () => {
    await Promise.all(
      whatsNewData.map(async ({ messageId, unread }: IWhatsNewComponent) => {
        if (unread) {
          await setWhatsNewAsReadQuery({ variables: { messageId } })
          setUnreadWhatsNews((news) => news - 1)
        }
        return Promise.resolve()
      })
    )
  }

  const closePopupWithoutHint = () => {
    setShowPopupNewsModal(false)
    // Fire-and-forget: a failed mark-as-read only leaves the badge until the next fetch.
    void handleUnreadNews().catch(() => undefined)
  }

  const closePopup = () => {
    closePopupWithoutHint()
    // The hint is shown on the Vee launcher, which is not mounted when Vee is off.
    if (showPopupNews && isVeeEnabled) {
      setPendingWhatsNewHintId(authUser.id, showPopupNews.messageId)
      dispatch(setPendingHint(showPopupNews))
    }
  }

  return { unreadWhatsNews, showPopupNewsModal, showPopupNews, closePopup, closePopupWithoutHint }
}
